/**
 * Removes credential text from a message, a stack or any other free text that
 * may be logged, shown or exported as telemetry (W-b8299199): `Bearer` and
 * `DPoP` values, values of credential-named keys (`access_token=…`,
 * `credential: …`, `"secret":"…"`), JWT-like strings, and URL query strings
 * and fragments.
 *
 * Linear time: the input is capped first, every quantifier is bounded, and no
 * pattern repeats inside a repetition or can restart inside a run it already
 * scanned. A message from an outside service can be any size; a summary or a
 * log line never needs more than the cap.
 */

export const MAX_REDACTED_TEXT_LENGTH = 4_096;
export const REDACTED = "[redacted]";
const TRUNCATED = " …[truncated]";

// Whole words that follow "Bearer" or "DPoP" in prose ("the DPoP proof.", "a
// bearer token"); only when the word ends there. Anything else after the
// scheme word is its value: redacted through the end of the line, and through
// following lines that continue it (indented, or a single token on its own).
const SCHEME_VALUE =
  /\b(Bearer|DPoP)([ \t]{1,8})(?!(?:proofs?|tokens?|access|keys?|signer|headers?|auth|authentication|authorization|nonces?|bound|scheme|session|credentials?)(?:[\s.,;:!?)]|$))[^\r\n]{1,4096}(?:\r?\n(?:[ \t][^\r\n]{0,4096}|[^\s]{1,4096}(?=\r?\n|$))){0,4}/gi;

// A URL (scheme, or a path starting with "/") followed by "?" or "#": the
// query string or fragment goes, the address stays.
const URL_QUERY =
  /((?<![A-Za-z0-9+.-])[A-Za-z][A-Za-z0-9+.-]{0,31}:\/\/[^\s?#"'<>]{0,2048}|(?<![^\s"'(=:])\/[^\s?#"'<>]{0,2048})[?#][^\s"'<>()]{0,4096}/g;

// Three base64url segments joined by dots, starting where a run of those
// characters starts, so a run without dots is scanned once.
const JWT_LIKE =
  /(?<![A-Za-z0-9_-])[A-Za-z0-9_-]{10,2048}\.[A-Za-z0-9_-]{10,4096}\.[A-Za-z0-9_-]{0,4096}/g;

// `key=value`, `key: value`, `"key":"value"` and `'key': 'value'`, with the key
// checked by name below.
const KEY_VALUE =
  /(?<![A-Za-z0-9_.-])(["']?)([A-Za-z][A-Za-z0-9_.-]{0,63})\1([ \t]{0,8}[:=][ \t]{0,8})(["']?)([^\s"'&,;)}[\]<>]{1,4096})/g;
const CREDENTIAL_KEY =
  /(?:access|refresh|id)?_?token|credential|secret|password|passwd|authorization|bearer|dpop|signature|api_?key|client_?secret|code_verifier/i;

export function redactCredentialText(text: string): string {
  const capped =
    text.length > MAX_REDACTED_TEXT_LENGTH
      ? `${text.slice(0, MAX_REDACTED_TEXT_LENGTH)}${TRUNCATED}`
      : text;
  return capped
    .replace(SCHEME_VALUE, `$1$2${REDACTED}`)
    .replace(URL_QUERY, `$1?${REDACTED}`)
    .replace(JWT_LIKE, REDACTED)
    .replace(
      KEY_VALUE,
      (match, keyQuote: string, key: string, separator: string, valueQuote: string) =>
        CREDENTIAL_KEY.test(key)
          ? `${keyQuote}${key}${keyQuote}${separator}${valueQuote}${REDACTED}`
          : match,
    );
}
